import { CheckCircleIcon as SolidCheckCircleIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { BookOpenIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationTriangleIcon, LockClosedIcon, SparklesIcon } from '@heroicons/react/24/outline';
import LmsAssignmentQuiz, { formatAssignmentAdorners } from 'pollworker/src/components/LmsAssignmentQuiz';
import PageEmptyState from 'pollworker/src/components/PageEmptyState';
import {
  completeLmsActivity,
  enrollLmsCourse,
  formatLmsQuizScore,
  getLmsAssignment,
  getLmsCourse,
  mapSubmissionToQuestions,
  pwwLmsMediaFetchers,
  retryLmsAssignment,
  submitLmsAssignment,
} from 'pollworker/src/fetchers';
import { LmsCourseContentUpdatedError } from 'pollworker/src/fetchers/lms';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'shared/src/components';
import LearnHouseContentRenderer, { LearnHouseYoutubeEmbed, UnsupportedBlock } from 'shared/src/components/LearnHouse/LearnHouseContentRenderer';
import LearnHousePdfViewer from 'shared/src/components/LearnHouse/LearnHousePdfViewer';
import {
  DOCUMENT_PDF_SUB_TYPE,
  DYNAMIC_EMBED_SUB_TYPE,
  DYNAMIC_PAGE_SUB_TYPE,
  isLearnHouseAssignmentActivity,
  parseLearnHouseEmbedContent,
} from 'shared/src/components/LearnHouse/learnHouseContent';
import { LmsMediaProvider } from 'shared/src/components/LearnHouse/lmsMediaContext';
import { Button, Dialog, DialogContent, Progress, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from 'shared/src/components/ui/new';
import type { LmsActivity, LmsAssignment, LmsAssignmentSubmitTask, LmsChapter, LmsCourseDetail, LmsQuizSummary } from 'shared/src/types/lms';
import { cn } from 'shared/src/utils';
import { LmsEnrollmentStatus, LmsQuizStatus } from 'shared/src/utils/lmsStatus';
import { toast } from 'sonner';
import { useLocation, useParams } from 'wouter';

type FlatActivity = {
  activity: LmsActivity;
  chapter: LmsChapter;
};

type QuizResultDialogState = boolean | null;

function isDynamicPage(activity: LmsActivity) {
  return activity.activity_sub_type === DYNAMIC_PAGE_SUB_TYPE;
}

function isDynamicEmbed(activity: LmsActivity) {
  return activity.activity_sub_type === DYNAMIC_EMBED_SUB_TYPE;
}

function isDocumentPdf(activity: LmsActivity) {
  return activity.activity_sub_type === DOCUMENT_PDF_SUB_TYPE;
}

function isAssignment(activity: LmsActivity) {
  return isLearnHouseAssignmentActivity(activity);
}

function isRenderable(activity: LmsActivity) {
  return isDynamicPage(activity) || isDynamicEmbed(activity) || isDocumentPdf(activity) || isAssignment(activity);
}

function flattenActivities(chapters: LmsChapter[]): FlatActivity[] {
  return chapters.flatMap((chapter) => (chapter.activities ?? []).map((activity) => ({ activity, chapter })));
}

function mergeSubmittedAnswers(assignment: LmsAssignment, tasks: LmsAssignmentSubmitTask[]): LmsAssignment {
  return {
    ...assignment,
    Tasks: assignment.Tasks.map((task) => {
      if (Object.keys(task.SelectedAnswerIdsByQuestion).length > 0) {
        return task;
      }

      const submitted = tasks.find((item) => item.TaskUuid === task.TaskUuid);
      if (!submitted) {
        return task;
      }

      const selected: Record<string, string[]> = submitted.TaskSubmission.submissions.reduce(
        (acc, answer) => {
          const existing = acc[answer.questionUUID] ?? [];
          acc[answer.questionUUID] = [...existing, answer.optionUUID];
          return acc;
        },
        {} as Record<string, string[]>,
      );

      return {
        ...task,
        SelectedAnswerIdsByQuestion: mapSubmissionToQuestions(task.Questions, selected),
      };
    }),
  };
}

function getQuizResultDialogState(enrollmentStatus: LmsEnrollmentStatus, quizzes: LmsQuizSummary[]): QuizResultDialogState {
  if (enrollmentStatus === LmsEnrollmentStatus.Completed && quizzes.length > 0 && quizzes.some((quiz) => !quiz.Passed)) {
    return false;
  }

  if (enrollmentStatus === LmsEnrollmentStatus.Completed && quizzes.length > 0 && quizzes.every((quiz) => quiz.Passed)) {
    return true;
  }

  return null;
}

function getCompletionStatus(enrollmentStatus: LmsEnrollmentStatus, quizzes: LmsQuizSummary[]): QuizResultDialogState {
  if (enrollmentStatus !== LmsEnrollmentStatus.Completed) {
    return null;
  }

  if (quizzes.every((quiz) => quiz.Passed || quizzes.length === 0)) {
    return true;
  }

  if (quizzes.every((quiz) => quiz.Status === LmsQuizStatus.Graded) && quizzes.some((quiz) => !quiz.Passed)) {
    return false;
  }

  return null;
}

function unlockedActivityUuids(activities: LmsActivity[]) {
  const unlocked = new Set<string>();
  let everythingBeforeIsDone = true;
  for (const activity of activities) {
    if (everythingBeforeIsDone || activity.is_completed) {
      unlocked.add(activity.activity_uuid);
    }
    if (activity.published && !activity.is_completed) {
      everythingBeforeIsDone = false;
    }
  }
  return unlocked;
}

const REACHED_END_SLACK_PX = 24;

function isScrolledToEnd(pane: HTMLElement) {
  return pane.scrollHeight - pane.scrollTop - pane.clientHeight <= REACHED_END_SLACK_PX;
}

function activityLabel(entry: FlatActivity, index: number) {
  return `${index + 1}. ${entry.activity.name}`;
}

function activityFromSearch() {
  return new URLSearchParams(window.location.search).get('activity') ?? '';
}

function sessionFromSearch() {
  return new URLSearchParams(window.location.search).get('sessionId') ?? '';
}

function PersistentScroll({
  as: Tag = 'div',
  className,
  scrollClassName,
  children,
  paneRef,
  testId,
  label,
  watch,
}: {
  as?: 'div' | 'nav';
  className?: string;
  scrollClassName?: string;
  children: ReactNode;
  paneRef?: (pane: HTMLDivElement | null) => void;
  testId?: string;
  label?: string;
  watch?: string | null;
}) {
  const pane = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number } | null>(null);

  const measure = useCallback(() => {
    const node = pane.current;
    if (!node) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = node;
    const trackHeight = Math.max(clientHeight - 8, 0);
    if (scrollHeight <= clientHeight + 1 || trackHeight <= 0) {
      setThumb(null);
      return;
    }

    const height = Math.max((clientHeight / scrollHeight) * trackHeight, 28);
    const maxScroll = scrollHeight - clientHeight;
    const maxTop = Math.max(trackHeight - height, 0);
    setThumb({
      height,
      top: maxScroll <= 0 ? 0 : (scrollTop / maxScroll) * maxTop,
    });
  }, []);

  const setPane = useCallback(
    (node: HTMLDivElement | null) => {
      pane.current = node;
      paneRef?.(node);
    },
    [paneRef],
  );

  useEffect(() => {
    measure();
    const node = pane.current;
    if (!node) {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (watch !== undefined && node.firstElementChild) {
      observer.observe(node.firstElementChild);
    }

    return () => observer.disconnect();
  }, [measure, watch]);

  return (
    <Tag aria-label={label} className={cn('relative min-h-0', className)}>
      <div ref={setPane} data-testid={testId} onScroll={measure} className={cn('ev-scroll absolute inset-0 overflow-y-scroll overscroll-contain', scrollClassName)}>
        {children}
      </div>
      {thumb ? (
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-1 right-1 w-2 rounded-full bg-gray-200">
          <div className="absolute inset-x-0 top-0 rounded-full bg-gray-500" style={{ height: thumb.height, transform: `translateY(${thumb.top}px)` }} />
        </div>
      ) : null}
    </Tag>
  );
}

function EmbedActivityContent({ activity }: { activity: LmsActivity }) {
  const embed = parseLearnHouseEmbedContent(activity.content);
  if (!embed) {
    return <UnsupportedBlock label="Unsupported embed content" payload={activity.content ?? null} />;
  }

  return <LearnHouseYoutubeEmbed source={embed.embed_url} title={activity.name} />;
}

export default function LmsCoursePlayer() {
  const { courseUuid } = useParams<{ courseUuid: string }>();
  const [, navigate] = useLocation();
  const [sessionId] = useState(sessionFromSearch);
  const [course, setCourse] = useState<LmsCourseDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(sessionId));
  const [error, setError] = useState<string | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [assignment, setAssignment] = useState<LmsAssignment | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [retryingQuiz, setRetryingQuiz] = useState(false);
  const [quizResultDialog, setQuizResultDialog] = useState<QuizResultDialogState>(null);
  const [reviewedUuid, setReviewedUuid] = useState<string | null>(null);
  const initialActivityUuid = useRef(activityFromSearch());
  const hasSelectedInitial = useRef(false);
  const contentPaneRef = useRef<HTMLDivElement | null>(null);
  const [contentPane, setContentPane] = useState<HTMLDivElement | null>(null);
  const attachContentPane = useCallback((pane: HTMLDivElement | null) => {
    contentPaneRef.current = pane;
    setContentPane(pane);
  }, []);
  const selectedNavRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    setLoading(true);
    setError(null);
    hasSelectedInitial.current = false;
    initialActivityUuid.current = activityFromSearch();

    try {
      await enrollLmsCourse(courseUuid, sessionId);
      const detail = await getLmsCourse(courseUuid, sessionId);
      setCourse(detail);
    } catch (loadError) {
      console.error(loadError);
      setCourse(null);
      setError(loadError instanceof Error ? loadError.message : 'Failed to open this course.');
    } finally {
      setLoading(false);
    }
  }, [courseUuid, sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const chapters = course?.Chapters ?? [];
  const flatActivities = useMemo(() => flattenActivities(chapters), [chapters]);
  const quizzes = course?.Quizzes ?? [];
  const courseCompleted = course?.EnrollmentStatus === LmsEnrollmentStatus.Completed;
  const unlocked = useMemo(() => unlockedActivityUuids(flatActivities.map((entry) => entry.activity)), [flatActivities]);

  useEffect(() => {
    if (flatActivities.length === 0) {
      setSelectedUuid(null);
      return;
    }

    if (hasSelectedInitial.current) {
      return;
    }

    const firstIncomplete = flatActivities.find((entry) => entry.activity.published && !entry.activity.is_completed);
    const fromUrl = flatActivities.find((entry) => entry.activity.activity_uuid === initialActivityUuid.current && unlocked.has(entry.activity.activity_uuid));
    const firstRenderable = flatActivities.find((entry) => isRenderable(entry.activity) && unlocked.has(entry.activity.activity_uuid));
    const initial = firstIncomplete ?? fromUrl ?? firstRenderable ?? flatActivities[0];
    setSelectedUuid(initial.activity.activity_uuid);
    const url = new URL(window.location.href);
    url.searchParams.set('activity', initial.activity.activity_uuid);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    hasSelectedInitial.current = true;
  }, [flatActivities, unlocked]);

  const selectedIndex = flatActivities.findIndex((entry) => entry.activity.activity_uuid === selectedUuid);
  const selected = selectedIndex >= 0 ? flatActivities[selectedIndex] : null;
  const previous = selectedIndex > 0 ? flatActivities[selectedIndex - 1] : null;
  const next = selectedIndex >= 0 && selectedIndex < flatActivities.length - 1 ? flatActivities[selectedIndex + 1] : null;
  const selectedComplete = selected?.activity.is_completed ?? false;
  const selectedIsAssignment = selected ? isAssignment(selected.activity) : false;
  const nextLocked = next ? !unlocked.has(next.activity.activity_uuid) : false;
  const selectedReviewed = selectedComplete || (selectedUuid !== null && reviewedUuid === selectedUuid);
  const completedCount = course?.CompletedActivityCount ?? 0;
  const totalCount = course?.TotalActivityCount ?? 0;
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  function selectActivity(activity: LmsActivity) {
    if (!unlocked.has(activity.activity_uuid)) {
      return;
    }

    setSelectedUuid(activity.activity_uuid);
    const url = new URL(window.location.href);
    url.searchParams.set('activity', activity.activity_uuid);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  const loadAssignment = useCallback(
    async (activityUuid: string) => {
      setAssignmentLoading(true);
      setAssignmentError(null);
      try {
        const nextAssignment = await getLmsAssignment(courseUuid, activityUuid, sessionId);
        setAssignment(nextAssignment);
      } catch (loadError) {
        console.error(loadError);
        setAssignment(null);
        setAssignmentError(loadError instanceof Error ? loadError.message : 'Failed to load this quiz');
      } finally {
        setAssignmentLoading(false);
      }
    },
    [courseUuid, sessionId],
  );

  useEffect(() => {
    if (!selectedUuid || !selectedIsAssignment) {
      setAssignment(null);
      setAssignmentError(null);
      setAssignmentLoading(false);
      return;
    }

    void loadAssignment(selectedUuid);
  }, [loadAssignment, selectedIsAssignment, selectedUuid]);

  useEffect(() => {
    if (!selectedUuid) {
      return;
    }

    const pane = contentPaneRef.current;
    if (pane) {
      pane.scrollTop = 0;
    }

    selectedNavRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selectedUuid]);

  useEffect(() => {
    const pane = contentPane;
    if (!selectedUuid || !pane) {
      return;
    }

    const check = () => {
      if (isScrolledToEnd(pane)) {
        setReviewedUuid(selectedUuid);
      }
    };

    check();
    pane.addEventListener('scroll', check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(pane);
    if (pane.firstElementChild) {
      observer.observe(pane.firstElementChild);
    }

    return () => {
      pane.removeEventListener('scroll', check);
      observer.disconnect();
    };
  }, [contentPane, selectedUuid]);

  async function handleQuizSubmit(tasks: LmsAssignmentSubmitTask[]) {
    if (!assignment || submittingQuiz) {
      return;
    }

    setSubmittingQuiz(true);
    setAssignmentError(null);
    try {
      await submitLmsAssignment(courseUuid, assignment.AssignmentUuid, tasks, sessionId);
      const detail = await getLmsCourse(courseUuid, sessionId);
      setCourse(detail);
      setQuizResultDialog(getQuizResultDialogState(detail.EnrollmentStatus, detail.Quizzes));
      const nextAssignment = await getLmsAssignment(courseUuid, assignment.ActivityUuid, sessionId);
      setAssignment(mergeSubmittedAnswers(nextAssignment, tasks));
    } catch (submitError) {
      console.error(submitError);
      setAssignmentError(submitError instanceof Error ? submitError.message : 'Failed to submit this quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  }

  async function handleQuizRetry() {
    if (!assignment || retryingQuiz) {
      return;
    }

    setRetryingQuiz(true);
    setAssignmentError(null);
    try {
      const nextAssignment = await retryLmsAssignment(courseUuid, assignment.AssignmentUuid, sessionId);
      const detail = await getLmsCourse(courseUuid, sessionId);
      setCourse(detail);
      setAssignment(nextAssignment);
    } catch (retryError) {
      console.error(retryError);
      setAssignmentError(retryError instanceof Error ? retryError.message : 'Failed to retry this quiz');
    } finally {
      setRetryingQuiz(false);
    }
  }

  async function markComplete() {
    if (!selected || selectedComplete || !selectedReviewed || completing) {
      return;
    }

    setCompleting(true);
    try {
      await completeLmsActivity(courseUuid, selected.activity.activity_uuid, sessionId);
      const detail = await getLmsCourse(courseUuid, sessionId);
      setCourse(detail);
      setQuizResultDialog(getCompletionStatus(detail.EnrollmentStatus, detail.Quizzes));
    } catch (completeError) {
      if (completeError instanceof LmsCourseContentUpdatedError) {
        toast('The content has been updated');
        await load();
        return;
      }

      console.error(completeError);
      setError(completeError instanceof Error ? completeError.message : 'Failed to mark this activity complete');
    } finally {
      setCompleting(false);
    }
  }

  if (!sessionId) {
    return (
      <div className="my-10">
        <PageEmptyState icon={BookOpenIcon} emptyMessageHeader="Open this course from My Training" emptyMessageSubHeader="Courses are started from the class you signed up for.">
          <Button className="mt-4" onClick={() => navigate('/training')}>
            Go to My Training
          </Button>
        </PageEmptyState>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center h-24 my-24 space-x-2">
        <Spinner show />
        <span className="text-lg">Opening course...</span>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="mt-2">
        <div className="my-10">
          <PageEmptyState icon={BookOpenIcon} errorMessage={error} emptyMessageHeader="Unable to open this course" emptyMessageSubHeader="Try again in a moment." reload={load} />
        </div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <LmsMediaProvider fetchers={pwwLmsMediaFetchers}>
      <Dialog open={quizResultDialog === true} onOpenChange={(open) => !open && setQuizResultDialog(null)}>
        <DialogContent>
          <div className="relative overflow-hidden bg-gradient-to-b from-emerald-50 via-white to-white px-6 pb-2 pt-10 text-center">
            <SparklesIcon className="absolute right-8 top-8 size-4 text-emerald-400" />
            <SparklesIcon className="absolute left-10 top-16 size-3 text-teal-300" />
            <CheckCircleIcon className="mx-auto size-16 text-emerald-600" />
            <h2 className="mt-4 text-xl font-semibold tracking-tight">Congratulations!</h2>
            <span className="mt-3 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">You completed {course?.Name}</span>
          </div>
          <div className="px-6 pt-1 text-center text-sm leading-relaxed text-gray-600">Nice work — you finished this course and met the training requirement. You're all set.</div>
          <div className="flex justify-center px-4 py-3 mt-2">
            <Button variant="outline" className="w-full" onClick={() => navigate('/training')}>
              ← Back to My Training
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={quizResultDialog === false} onOpenChange={(open) => !open && setQuizResultDialog(null)}>
        <DialogContent>
          <div className="relative overflow-hidden bg-gradient-to-b from-amber-50 via-white to-white px-6 pb-2 pt-10 text-center">
            <ExclamationTriangleIcon className="mx-auto size-14 text-amber-500" />
            <h2 className="mt-4 text-xl font-semibold tracking-tight">Not quite this time</h2>
            <span className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">You didn't pass {course?.Name}</span>
          </div>
          <div className="px-6 pt-3 text-center text-sm leading-relaxed text-gray-600">
            You didn't meet the passing score for this course. Head back to My Training when you're ready to try again.
          </div>
          <div className="flex justify-center px-4 py-3 mt-2">
            <Button variant="outline" className="w-full" onClick={() => navigate('/training')}>
              ← Back to My Training
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="mt-2 flex h-[calc(100dvh-8rem)] min-h-[28rem] flex-col gap-2">
        <div className="flex shrink-0 items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="mt-0.5 truncate text-base font-semibold leading-6 text-gray-900">{course.Name}</h1>
            {course.Description ? <p className="hidden text-sm text-gray-700 sm:line-clamp-2 sm:block">{course.Description}</p> : null}
          </div>
          {courseCompleted ? null : (
            <div className="w-28 shrink-0 space-y-0.5 sm:w-40 lg:max-w-xs">
              <div className="text-xs text-gray-600 sm:text-sm">
                {completedCount} / {totalCount} complete
              </div>
              <Progress value={percent} aria-label={`${course.Name} progress`} />
            </div>
          )}
        </div>

        {error ? <div className="shrink-0 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {courseCompleted ? (
          <div className="flex shrink-0 items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <CheckCircleIcon className="size-5 shrink-0 text-emerald-600" aria-hidden="true" />
            <span>You completed this course.</span>
          </div>
        ) : null}

        {chapters.length === 0 ? (
          <div className="text-sm text-gray-500">{courseCompleted ? 'The content you completed is no longer part of this course.' : 'This course has no chapters.'}</div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:flex-row">
            <div className="shrink-0 border-b border-gray-200 p-2 lg:hidden">
              {selectedUuid ? (
                <Select
                  value={selectedUuid}
                  onValueChange={(activityUuid) => {
                    const entry = flatActivities.find((item) => item.activity.activity_uuid === activityUuid);
                    if (entry) {
                      selectActivity(entry.activity);
                    }
                  }}
                >
                  <SelectTrigger size="sm" className="w-full max-w-none" aria-label="Course pages">
                    <SelectValue placeholder="Select a page" />
                  </SelectTrigger>
                  <SelectContent>
                    {chapters.map((chapter, chapterIndex) => (
                      <SelectGroup key={chapter.chapter_uuid}>
                        <SelectLabel>
                          {chapterIndex + 1}. {chapter.name}
                        </SelectLabel>
                        {(chapter.activities ?? []).map((activity) => {
                          const isComplete = activity.is_completed;
                          const quiz = quizzes.find((item) => item.ActivityUuid === activity.activity_uuid);
                          const quizScore = quiz && quiz.Status === LmsQuizStatus.Graded ? formatLmsQuizScore(quiz) : null;
                          const isLocked = !unlocked.has(activity.activity_uuid);
                          return (
                            <SelectItem key={activity.activity_uuid} value={activity.activity_uuid} disabled={isLocked}>
                              <span className="flex min-w-0 items-center gap-2">
                                {isLocked ? (
                                  <LockClosedIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                ) : isComplete ? (
                                  <SolidCheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                                ) : (
                                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
                                )}
                                <span className="truncate">{activity.name}</span>
                                {quizScore ? <span className="shrink-0 text-xs font-medium text-gray-500">{quizScore}</span> : null}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
            <PersistentScroll as="nav" label="Course chapters" watch={selectedUuid} className="hidden shrink-0 border-gray-200 bg-gray-50/80 lg:block lg:w-72 lg:border-r xl:w-80">
              <div className="p-3">
                {chapters.map((chapter, chapterIndex) => {
                  const activities = chapter.activities ?? [];
                  return (
                    <div key={chapter.chapter_uuid} className="mb-3 last:mb-0">
                      <div className="px-2 py-1 text-sm font-semibold text-gray-900">
                        {chapterIndex + 1}. {chapter.name}
                      </div>
                      {activities.length === 0 ? (
                        <div className="px-4 py-1 text-xs text-gray-400">No activities</div>
                      ) : (
                        <ul className="mt-1 space-y-0.5">
                          {activities.map((activity) => {
                            const isSelected = activity.activity_uuid === selectedUuid;
                            const isComplete = activity.is_completed;
                            const quiz = quizzes.find((item) => item.ActivityUuid === activity.activity_uuid);
                            const quizScore = quiz && quiz.Status === LmsQuizStatus.Graded ? formatLmsQuizScore(quiz) : null;
                            const isLocked = !unlocked.has(activity.activity_uuid);
                            return (
                              <li key={activity.activity_uuid}>
                                <button
                                  ref={isSelected ? selectedNavRef : undefined}
                                  type="button"
                                  disabled={isLocked}
                                  aria-current={isSelected ? 'page' : undefined}
                                  onClick={() => selectActivity(activity)}
                                  className={cn(
                                    'flex w-full items-center gap-2 truncate rounded-md px-3 py-1.5 text-left text-sm',
                                    isLocked ? 'cursor-not-allowed text-gray-400' : 'hover:bg-white',
                                    isSelected ? 'bg-white font-medium text-gray-900 shadow-sm ring-1 ring-gray-200' : !isLocked && 'text-gray-700',
                                  )}
                                >
                                  {isLocked ? (
                                    <LockClosedIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  ) : isComplete ? (
                                    <SolidCheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                                  ) : (
                                    <CheckCircleIcon className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
                                  )}
                                  <span className="truncate">{activity.name}</span>
                                  {quizScore ? <span className="ml-auto shrink-0 text-xs font-medium text-gray-500">{quizScore}</span> : null}
                                  {isComplete ? <span className="sr-only">completed</span> : null}
                                  {isLocked ? <span className="sr-only">locked until you finish the activities before it</span> : null}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </PersistentScroll>

            <section className="flex min-h-0 min-w-0 flex-1 flex-col">
              {selected ? (
                <>
                  <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-2 lg:px-5 lg:py-3">
                    <div className="min-w-0">
                      {selectedIsAssignment ? null : <div className="hidden text-xs uppercase tracking-wide text-gray-500 lg:block">{selected.chapter.name}</div>}
                      <h2 className="text-base font-semibold text-gray-900">{selected.activity.name}</h2>
                    </div>
                    {selectedIsAssignment && assignment ? <p className="shrink-0 text-right text-sm text-gray-600">{formatAssignmentAdorners(assignment)}</p> : null}
                  </div>
                  <PersistentScroll
                    paneRef={attachContentPane}
                    testId="lms-content-pane"
                    watch={selectedUuid}
                    className="flex-1"
                    scrollClassName="px-4 py-3 pr-6 lg:px-5 lg:py-4 lg:pr-7"
                  >
                    <div>
                      {isDynamicPage(selected.activity) ? (
                        <LearnHouseContentRenderer content={selected.activity.content} courseUuid={courseUuid} activityUuid={selected.activity.activity_uuid} />
                      ) : isDynamicEmbed(selected.activity) ? (
                        <EmbedActivityContent activity={selected.activity} />
                      ) : isDocumentPdf(selected.activity) ? (
                        <LearnHousePdfViewer courseUuid={courseUuid} activityUuid={selected.activity.activity_uuid} title={selected.activity.name} />
                      ) : isAssignment(selected.activity) ? (
                        assignmentLoading ? (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Spinner show />
                            Loading quiz...
                          </div>
                        ) : assignment ? (
                          <LmsAssignmentQuiz
                            key={`${assignment.AssignmentUuid}:${assignment.AttemptCount}:${assignment.Status}`}
                            assignment={assignment}
                            submitting={submittingQuiz}
                            retrying={retryingQuiz}
                            error={assignmentError}
                            onSubmit={(tasks) => void handleQuizSubmit(tasks)}
                            onRetry={() => void handleQuizRetry()}
                          />
                        ) : (
                          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{assignmentError ?? 'Failed to load this quiz'}</div>
                        )
                      ) : (
                        <UnsupportedBlock
                          label={`Not supported yet: ${selected.activity.activity_type} / ${selected.activity.activity_sub_type}`}
                          payload={selected.activity.content ?? null}
                        />
                      )}
                    </div>
                  </PersistentScroll>
                  <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 bg-white px-3 py-2 lg:px-5 lg:py-3">
                    <Button variant="outline" size="sm" className="min-w-0 max-w-[32%]" disabled={!previous} onClick={() => previous && selectActivity(previous.activity)}>
                      <ChevronLeftIcon className="h-4 w-4 lg:mr-1" />
                      <span className="truncate lg:hidden">Previous</span>
                      <span className="hidden truncate lg:inline">{previous ? activityLabel(previous, selectedIndex - 1) : 'Previous'}</span>
                    </Button>
                    {selectedIsAssignment ? (
                      <div className="hidden text-sm text-gray-500 sm:block">{selectedComplete ? 'Quiz completed' : 'Submit the quiz to mark this activity complete'}</div>
                    ) : selectedReviewed ? (
                      <Button size="sm" disabled={selectedComplete || completing} onClick={() => void markComplete()}>
                        {selectedComplete ? 'Completed' : completing ? 'Saving…' : 'Mark complete'}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled>
                        Scroll down to continue
                      </Button>
                    )}
                    <Button size="sm" className="min-w-0 max-w-[32%]" disabled={!next || nextLocked} onClick={() => next && selectActivity(next.activity)}>
                      <span className="truncate lg:hidden">Next</span>
                      <span className="hidden truncate lg:inline">{next ? next.activity.name : 'Next'}</span>
                      <ChevronRightIcon className="h-4 w-4 lg:ml-1" />
                      {nextLocked ? <span className="sr-only">locked until you finish the activities before it</span> : null}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="px-5 py-4 text-sm text-gray-500">This course has no activities to render.</div>
              )}
            </section>
          </div>
        )}
      </div>
    </LmsMediaProvider>
  );
}
